import React, { useState } from 'react'
import { Box, FormControl, IconButton, MenuItem, Select, TextField } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import { withComparator } from '../../../utils/tableConditionalFormat'
import type { TableConditionalFormatComparator, TableConditionalFormatRule } from '../../../types/gotypes'

const rowSx = { display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' } as const

/**
 * An ordered list of value-threshold rules (comparator, threshold, colour;
 * first match wins) with an "add rule" row under it. Shared by the table
 * widget's conditional formatting (#969 Part C) and the chart's bar colours
 * by value (#1808), which use the same rule model. The colour is the rule's
 * `background_color`: a cell's fill, or a bar's colour.
 *
 * Pure rendering plus the draft of the rule being added — the caller owns the
 * stored list.
 */
export const ConditionalFormatRuleList: React.FC<{
  rules: TableConditionalFormatRule[]
  onAdd: (rule: TableConditionalFormatRule) => void
  onUpdate: (index: number, rule: TableConditionalFormatRule) => void
  onRemove: (index: number) => void
  /** Tooltip on the colour swatches. */
  colorTitle?: string
  /** Starting threshold of the add row. */
  initialThreshold?: string
}> = ({ rules, onAdd, onUpdate, onRemove, colorTitle = 'Background color', initialThreshold = '50' }) => {
  const [comparator, setComparator] = useState<TableConditionalFormatComparator>('gte')
  const [threshold, setThreshold] = useState(initialThreshold)
  const [upper, setUpper] = useState(initialThreshold)
  const [color, setColor] = useState('#2E7D32')

  const handleAdd = () => {
    const value = Number(threshold)
    if (threshold.trim() === '' || Number.isNaN(value)) return
    if (comparator === 'between') {
      const high = Number(upper)
      if (upper.trim() === '' || Number.isNaN(high)) return
      onAdd({ comparator, threshold: value, threshold_upper: high, background_color: color })
      return
    }
    onAdd({ comparator, threshold: value, background_color: color })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {rules.map((rule, index) => (
        <Box key={index} sx={rowSx}>
          <ComparatorSelect
            value={rule.comparator}
            onChange={(next) => onUpdate(index, withComparator(rule, next))}
          />
          <TextField
            size="small"
            type="number"
            value={rule.threshold}
            inputProps={{ 'aria-label': rule.comparator === 'between' ? 'From value' : 'Value' }}
            onChange={(e) => onUpdate(index, { ...rule, threshold: Number(e.target.value) })}
            sx={{ width: 90 }}
          />
          {rule.comparator === 'between' && (
            <>
              <span>and</span>
              <TextField
                size="small"
                type="number"
                value={rule.threshold_upper ?? ''}
                inputProps={{ 'aria-label': 'To value' }}
                onChange={(e) =>
                  onUpdate(index, {
                    ...rule,
                    threshold_upper: e.target.value === '' ? rule.threshold : Number(e.target.value),
                  })
                }
                sx={{ width: 90 }}
              />
            </>
          )}
          <input
            type="color"
            title={colorTitle}
            value={rule.background_color || '#ffffff'}
            onChange={(e) => onUpdate(index, { ...rule, background_color: e.target.value })}
            style={{ width: 24, height: 24, border: 'none', cursor: 'pointer', padding: 0, background: 'none' }}
          />
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" onClick={() => onRemove(index)}>
            <DeleteIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      ))}
      <Box sx={rowSx}>
        <ComparatorSelect value={comparator} onChange={setComparator} />
        <TextField
          size="small"
          type="number"
          placeholder={comparator === 'between' ? 'From' : 'Threshold'}
          value={threshold}
          onChange={(e) => setThreshold(e.target.value)}
          sx={{ width: 90 }}
        />
        {comparator === 'between' && (
          <>
            <span>and</span>
            <TextField
              size="small"
              type="number"
              placeholder="To"
              value={upper}
              onChange={(e) => setUpper(e.target.value)}
              sx={{ width: 90 }}
            />
          </>
        )}
        <input
          type="color"
          title={colorTitle}
          value={color}
          onChange={(e) => setColor(e.target.value)}
          style={{ width: 24, height: 24, border: 'none', cursor: 'pointer', padding: 0, background: 'none' }}
        />
        <IconButton size="small" onClick={handleAdd} disabled={threshold.trim() === '' || (comparator === 'between' && upper.trim() === '')} color="info">
          <AddIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Box>
  )
}

const ComparatorSelect: React.FC<{
  value: TableConditionalFormatComparator
  onChange: (value: TableConditionalFormatComparator) => void
}> = ({ value, onChange }) => (
  <FormControl size="small" sx={{ minWidth: 200 }}>
    <Select value={value} onChange={(e) => onChange(e.target.value as TableConditionalFormatComparator)}>
      <MenuItem value="gt">greater than</MenuItem>
      <MenuItem value="gte">greater than or equal to</MenuItem>
      <MenuItem value="lt">less than</MenuItem>
      <MenuItem value="lte">less than or equal to</MenuItem>
      <MenuItem value="eq">equal to</MenuItem>
      <MenuItem value="between">between</MenuItem>
    </Select>
  </FormControl>
)

export default ConditionalFormatRuleList
